import type { IconNames } from "@/v2-ui/icon/icon.types"
import { useLocation } from "@/v2-backoffice-client/utils"
import Sidebar from "@/v2-backoffice-client/components/sidebar"
import { useIsAdvancedViewMode } from "@/v2-backoffice-client/settings/settings.config"
import {
  FEATURE_PAGE_PATH,
  IS_TRANSLATION_ENABLED,
} from "@/v2-backoffice-client/translation/translation.constants"
import { LICENSING_PATH } from "@/v2-backoffice-client/license/license.constants"
import { ACTIVITY_PATH } from "@/v2-backoffice-client/signIn/signIn.constants"
import LayerReviewCountBadge from "@/v2-backoffice-client/layer/review/LayerReviewCountBadge"
import NotificationCenter from "@/v2-backoffice-client/notification/NotificationCenter"

type NavItem = {
  id: string
  path: string
  label: string
  icon: IconNames
  advancedOnly?: boolean
  isHidden?: boolean
}

const NAV_ITEMS: NavItem[] = [
  {
    id: "customers",
    path: "/customers",
    label: "Customers",
    icon: "users-alt",
  },
  {
    id: "licensing",
    path: LICENSING_PATH,
    label: "Licensing",
    icon: "receipt",
  },
  {
    id: "tenants",
    path: "/tenants",
    label: "Teams",
    icon: "users",
  },
  {
    id: "users",
    path: "/users",
    label: "Users",
    icon: "user",
  },
  {
    id: "invitations",
    path: "/invitations",
    label: "Invitations",
    icon: "envelope",
  },
  {
    id: "agent-conversations",
    path: "/agent-conversations",
    label: "Agent",
    icon: "chatbot",
  },
  {
    id: "activity",
    path: ACTIVITY_PATH,
    label: "Activity",
    icon: "user-key",
  },
  {
    id: "layers",
    path: "/layers",
    label: "Layers",
    icon: "layers",
    advancedOnly: true,
  },
  // {
  //   id: "feature-flags",
  //   path: "/feature-flags",
  //   label: "Features",
  //   icon: "flag",
  //   advancedOnly: true,
  // },
  {
    id: "uptime",
    path: "/uptime",
    label: "Uptime",
    icon: "pulse",
  },
  // {
  //   id: "analytics",
  //   path: "/analytics",
  //   label: "Analytics",
  //   icon: "chart-histogram",
  // },
  {
    id: "legal-billing",
    path: "/legal-billing",
    label: "Legal billing",
    icon: "file-invoice",
    advancedOnly: true,
  },
  {
    id: "features",
    path: FEATURE_PAGE_PATH,
    label: "Features",
    icon: "language",
    isHidden: !IS_TRANSLATION_ENABLED,
  },
]

function AppSidebar() {
  const location = useLocation()

  const isAdvanced = useIsAdvancedViewMode()
  const visibleItems = NAV_ITEMS.filter(
    (item) => !item.isHidden && (!item.advancedOnly || isAdvanced),
  )

  return (
    <Sidebar
      className="layout-sidebar border-r border-border h-full flex flex-col"
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Fixed h-12 with border-b (not a separate hr) so the under-logo
          line sits at the exact same y as the notification dock header's
          border-b, in both full and mini sidebar modes. */}
      <Sidebar.Branding className="h-12 shrink-0 border-b border-border px-2" />

      <div className="p-2 space-y-0.5">
        {visibleItems.map((item) => {
          const isActive =
            item.path === "/"
              ? location.pathname === "/" ||
                location.pathname.startsWith("/customers")
              : location.pathname.startsWith(item.path)

          return (
            <Sidebar.Link
              key={item.id}
              to={item.path}
              iconSrc={item.icon}
              isActive={isActive}
              badge={item.id === "layers" ? <LayerReviewCountBadge /> : null}
            >
              {item.label}
            </Sidebar.Link>
          )
        })}
      </div>

      <hr className="h-px mt-auto bg-border" />

      <div className="p-2 space-y-0.5">
        <NotificationCenter />
        <Sidebar.Link
          to="/settings"
          iconSrc="settings"
          isActive={location.pathname.startsWith("/settings")}
        >
          Settings
        </Sidebar.Link>
        <Sidebar.Link to="/logout" iconSrc="lock">
          Logout
        </Sidebar.Link>
      </div>
    </Sidebar>
  )
}

export default AppSidebar
